import {
  createClient,
  type Session,
  type SupabaseClient,
  type User,
} from "@supabase/supabase-js";

// 배포 환경변수에 섞이기 쉬운 앞뒤 공백·개행·따옴표를 제거한다.
// (값에 개행이 남으면 fetch가 "Invalid value" TypeError를 던진다.)
const cleanEnv = (value: string | undefined): string =>
  (value ?? "").trim().replace(/^["']+|["']+$/g, "");

const SUPABASE_URL = cleanEnv(import.meta.env.VITE_SUPABASE_URL);
const SUPABASE_ANON_KEY = cleanEnv(import.meta.env.VITE_SUPABASE_ANON_KEY);

if (!SUPABASE_URL) {
  throw new Error("VITE_SUPABASE_URL is not defined. Please check your environment settings.");
}

if (!/^https:\/\/[a-z0-9-]+\.supabase\.co$/.test(SUPABASE_URL)) {
  throw new Error(`VITE_SUPABASE_URL 값이 올바른 형식이 아닙니다: "${SUPABASE_URL}"`);
}

if (!SUPABASE_ANON_KEY) {
  throw new Error("VITE_SUPABASE_ANON_KEY is not defined. Please check your environment settings.");
}

export const supabase: SupabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storageKey: "classlive-supabase-auth",
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

let anonymousSessionPromise: Promise<{ session: Session; user: User }> | null = null;

/**
 * The student app and the teacher app can mount at the same time (the teacher
 * app is rendered by App). Share the anonymous sign-in request so concurrent
 * mounts cannot replace each other's Supabase session.
 */
export function ensureAnonymousSession(): Promise<{ session: Session; user: User }> {
  if (anonymousSessionPromise) {
    return anonymousSessionPromise;
  }

  anonymousSessionPromise = (async () => {
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) {
      throw sessionError;
    }

    const existingSession = sessionData.session;
    const expiresAt = (existingSession?.expires_at ?? 0) * 1000;
    if (existingSession?.user && (!expiresAt || expiresAt > Date.now() + 30_000)) {
      return { session: existingSession, user: existingSession.user };
    }

    if (existingSession?.user) {
      const { data: refreshedData, error: refreshError } =
        await supabase.auth.refreshSession();
      if (!refreshError && refreshedData.session?.user) {
        return { session: refreshedData.session, user: refreshedData.session.user };
      }
    }

    const { data: signInData, error: signInError } =
      await supabase.auth.signInAnonymously();
    if (signInError) {
      throw signInError;
    }
    if (!signInData.session || !signInData.user) {
      throw new Error("Supabase 익명 세션을 만들지 못했습니다.");
    }

    return { session: signInData.session, user: signInData.user };
  })().catch((error) => {
    anonymousSessionPromise = null;
    throw error;
  });

  return anonymousSessionPromise;
}

export type { SupabaseClient };
